import type { FormFieldDefinition, FormSection } from '../types/formBuilder';
import { evaluateVisibilityCondition } from './stepVisibility';

export interface RuntimeFormStep {
  section: FormSection;
  fields: FormFieldDefinition[];
}

function hasValue(value: unknown) {
  if (Array.isArray(value)) return value.length > 0;
  return value !== null && value !== undefined && value !== '';
}

export function getStepOrder(step: RuntimeFormStep, formData: Record<string, unknown>) {
  const baseOrder = step.section.baseOrder ?? step.section.stepOrder ?? step.section.sortOrder ?? 0;
  const dynamicOrder = step.section.dynamicOrder;
  if (!dynamicOrder?.fieldKey || !dynamicOrder.mapping) return baseOrder;

  const value = formData[dynamicOrder.fieldKey];
  const mapped = dynamicOrder.mapping[String(value)];
  return typeof mapped === 'number' ? mapped : baseOrder;
}

export function orderStepsDynamically(
  steps: RuntimeFormStep[],
  formData: Record<string, unknown>,
) {
  return [...steps].sort((left, right) => {
    const orderDelta = getStepOrder(left, formData) - getStepOrder(right, formData);
    if (orderDelta !== 0) return orderDelta;
    return (left.section.sortOrder ?? 0) - (right.section.sortOrder ?? 0);
  });
}

export function calculateStepScore(step: RuntimeFormStep, formData: Record<string, unknown>) {
  let score = 0;

  if (step.section.priority === 'HIGH') score += 50;
  if (step.section.priority === 'LOW') score -= 10;

  const requiredFields = step.section.requiredFields?.length
    ? step.section.requiredFields
    : step.fields.filter((field) => field.mandatory).map((field) => field.internalName);

  if (requiredFields.some((fieldKey) => !hasValue(formData[fieldKey]))) {
    score += 30;
  }

  if (
    step.section.contextRelevance
    && Object.values(formData).some((value) => String(value) === step.section.contextRelevance)
  ) {
    score += 40;
  }

  const incompleteVisibleFields = step.fields.filter((field) =>
    evaluateVisibilityCondition(field.visibilityCondition, formData)
      && field.mandatory
      && !hasValue(formData[field.internalName]),
  );
  score += incompleteVisibleFields.length * 5;

  return score;
}

export function getRecommendedNextStep(
  steps: RuntimeFormStep[],
  formData: Record<string, unknown>,
  completedStepIds: Set<string>,
) {
  const candidates = steps.filter((step) =>
    evaluateVisibilityCondition(step.section.visibilityCondition, formData)
      && !completedStepIds.has(step.section.sectionId),
  );

  const scored = candidates.map((step) => ({
    step,
    score: calculateStepScore(step, formData),
  }));

  return scored.sort((a, b) => b.score - a.score)[0]?.step;
}